/**
 * Central locale-aware date/time formatting driven by Regional Settings (admin).
 * Values are synced from RegionalSettingsContext on app load.
 *
 * Regional (localeTag / dateFormat): numeric date order, separators, currency grouping.
 * UI language (syncUiLanguage): spoken month/weekday names — must match the header language selector.
 *
 * timeFormat: HOUR_24 (default) | HOUR_12
 * timezone: IANA zone from regional_settings (academy wall time)
 */

const DEFAULTS = {
  localeTag: 'en-GB',
  dateFormat: 'dd/MM/yyyy',
  datetimeFormat: 'dd/MM/yyyy HH:mm',
  timeFormat: 'HOUR_24',
  timezone: 'UTC',
};

const UI_LOCALE_BY_LANG = {
  en: 'en-GB',
  de: 'de-DE',
  fr: 'fr-FR',
  nl: 'nl-NL',
};

let settings = { ...DEFAULTS };
/** ISO 639-1 language from i18n (en / de / fr / nl). */
let uiLanguage = 'en';

export function syncRegionalFormatSettings(next) {
  if (!next) return;
  settings = {
    ...DEFAULTS,
    ...next,
    timeFormat: next.timeFormat === 'HOUR_12' ? 'HOUR_12' : 'HOUR_24',
    timezone: next.timezone || DEFAULTS.timezone,
  };
}

/** Sync with react-i18next language (Header selector / localStorage.uiLanguage). */
export function syncUiLanguage(lang) {
  const raw = String(lang || 'en').split('-')[0].toLowerCase();
  uiLanguage = Object.prototype.hasOwnProperty.call(UI_LOCALE_BY_LANG, raw) ? raw : 'en';
}

export function getUiLanguage() {
  return uiLanguage;
}

/** BCP 47 tag for month/weekday names matching the selected UI language. */
export function getUiLocaleTag() {
  return UI_LOCALE_BY_LANG[uiLanguage] || UI_LOCALE_BY_LANG.en;
}

export function getRegionalFormatSettings() {
  return { ...settings };
}

function usesLanguageNames(options = {}) {
  const month = options.month;
  const weekday = options.weekday;
  const monthIsNamed = month != null && month !== 'numeric' && month !== '2-digit';
  return monthIsNamed || weekday != null;
}

function localeForIntlOptions(options = {}) {
  return usesLanguageNames(options)
    ? getUiLocaleTag()
    : (settings.localeTag || DEFAULTS.localeTag);
}

/** Academy IANA timezone from Admin → Regional Settings. */
export function getAcademyTimeZone() {
  return settings.timezone || DEFAULTS.timezone;
}

/** Calendar parts for "now" in the academy timezone (not browser local). */
export function academyNowParts() {
  const tz = getAcademyTimeZone();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return {
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      hour: Number(get('hour')),
      minute: Number(get('minute')),
      second: Number(get('second')),
    };
  } catch {
    return partsFromTimeZone(DEFAULTS.timezone);
  }
}

function partsFromTimeZone(tz) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return {
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      hour: Number(get('hour')),
      minute: Number(get('minute')),
      second: Number(get('second')),
    };
  } catch {
    const d = new Date();
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      second: d.getUTCSeconds(),
    };
  }
}

/** Today's calendar date in academy timezone (local Date at midnight wall time). */
export function academyTodayDate() {
  const p = academyNowParts();
  return new Date(p.year, p.month - 1, p.day);
}

/** Current wall-clock moment as a local Date built from academy timezone parts. */
export function academyNowDate() {
  const p = academyNowParts();
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Today as ISO YYYY-MM-DD in academy timezone. */
export function todayIso() {
  const p = academyNowParts();
  return `${String(p.year).padStart(4, '0')}-${pad(p.month)}-${pad(p.day)}`;
}

/** Calendar year in academy timezone (e.g. footer copyright). */
export function academyYear() {
  return academyNowParts().year;
}

/** Short label for footer / build info (e.g. CET, IST). */
export function getAcademyTimezoneLabel() {
  const tz = getAcademyTimeZone();
  try {
    const parts = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
}

export function usesHour12() {
  return settings.timeFormat === 'HOUR_12';
}

function isZonedIsoString(s) {
  const t = String(s).trim();
  return /[zZ]$/.test(t) || /[+\-]\d{2}:\d{2}$/.test(t);
}

function isNaiveIsoDateTime(s) {
  const t = String(s).trim();
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(t) && !isZonedIsoString(t);
}

/** Parse API naive LocalDateTime as academy wall-time digits (not browser TZ). */
function parseNaiveWallDateTime(s) {
  const m = String(s).trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (!m) return null;
  const year = +m[1];
  const month = +m[2];
  const day = +m[3];
  const hour = m[4] != null ? +m[4] : 0;
  const minute = m[5] != null ? +m[5] : 0;
  const second = m[6] != null ? +m[6] : 0;
  return new Date(year, month - 1, day, hour, minute, second);
}

export function toDateSafe(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (isNaiveIsoDateTime(s)) {
      return parseNaiveWallDateTime(s);
    }
    if (isZonedIsoString(s)) {
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return parseNaiveWallDateTime(`${s}T00:00:00`);
    }
    if (/^\d{1,2}:\d{2}$/.test(s)) {
      const [h, m] = s.split(':').map(Number);
      const d = academyNowDate();
      d.setHours(h, m, 0, 0);
      return d;
    }
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function isWallTimeValue(value) {
  return typeof value === 'string' && (isNaiveIsoDateTime(value) || /^\d{4}-\d{2}-\d{2}$/.test(value.trim()));
}

function intlDateTimeOptions(extra = {}) {
  return {
    timeZone: getAcademyTimeZone(),
    hour12: usesHour12(),
    ...extra,
  };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function getDatePattern() {
  return settings.dateFormat || DEFAULTS.dateFormat;
}

function getDatetimePattern() {
  return settings.datetimeFormat
    || defaultDatetimeFormatForTimeFormat(settings.timeFormat, settings.dateFormat);
}

function dateTokensFromPattern(pattern) {
  return pattern.split(/[^dMy]+/).filter(Boolean);
}

function isValidCalendarDate(year, month, day) {
  const dt = new Date(year, month - 1, day);
  return !isNaN(dt.getTime())
    && dt.getFullYear() === year
    && dt.getMonth() === month - 1
    && dt.getDate() === day;
}

/** Format a Date using the regional date pattern (for pickers and inputs). */
export function formatDateInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : toDateSafe(date);
  if (!d) return '';
  const pattern = getDatePattern();
  return pattern
    .replace(/yyyy/g, String(d.getFullYear()))
    .replace(/MM/g, pad(d.getMonth() + 1))
    .replace(/dd/g, pad(d.getDate()));
}

/** Parse a regional date string (flexible / - or . separator) to Date. */
export function parseDateInput(str) {
  if (!str) return null;
  const trimmed = String(str).trim();
  // Accept ISO yyyy-MM-dd regardless of regional pattern (API/display round-trips).
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    if (!isValidCalendarDate(y, m, d)) return null;
    return new Date(y, m - 1, d);
  }
  const tokens = dateTokensFromPattern(getDatePattern());
  if (tokens.length !== 3) return null;

  const regexParts = tokens.map((token) => (
    token === 'yyyy' ? '(\\d{4})' : '(\\d{1,2})'
  ));
  const regex = new RegExp(`^${regexParts.join('[\\/\\-\\.]')}$`);
  const m = trimmed.match(regex);
  if (!m) return null;

  let day;
  let month;
  let year;
  tokens.forEach((token, i) => {
    const val = parseInt(m[i + 1], 10);
    if (token === 'dd') day = val;
    else if (token === 'MM') month = val;
    else if (token === 'yyyy') year = val;
  });

  if (!day || !month || !year) return null;
  if (!isValidCalendarDate(year, month, day)) return null;
  return new Date(year, month - 1, day);
}

/** Regional date input → ISO date YYYY-MM-DD */
export function toISODateFromInput(str) {
  const d = parseDateInput(str);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** True when value parses to a calendar date that is today or earlier (academy timezone). */
export function isDateOnOrBeforeToday(str) {
  const d = parseDateInput(str);
  if (!d) return false;
  const today = academyTodayDate();
  const candidate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return candidate.getTime() <= todayMidnight.getTime();
}

/** ISO date → regional date input string */
export function toRegionalDateFromISO(iso) {
  if (!iso) return '';
  return formatDateInput(toDateSafe(iso));
}

export function getDateFormatPlaceholder() {
  return getDatePattern().toUpperCase();
}

export function getDateTimeFormatPlaceholder() {
  return getDatetimePattern().toUpperCase();
}

export function getTimeFormatPlaceholder() {
  return usesHour12() ? 'HH:MM AM' : 'HH:MM';
}

function parseTimePart(timeStr) {
  if (!timeStr) return null;
  const s = String(timeStr).trim();
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = parseInt(m12[2], 10);
    const pm = m12[3].toUpperCase() === 'PM';
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    return { h, min };
  }
  const m24 = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m24) {
    return { h: parseInt(m24[1], 10), min: parseInt(m24[2], 10) };
  }
  return null;
}

function dayPeriodForHour(hour) {
  try {
    const d = new Date(2000, 0, 1, hour, 0, 0);
    const parts = new Intl.DateTimeFormat(getUiLocaleTag(), {
      hour: 'numeric',
      hour12: true,
    }).formatToParts(d);
    let period = parts.find((p) => p.type === 'dayPeriod')?.value
      || (hour >= 12 ? 'PM' : 'AM');
    // English UI expects conventional uppercase AM/PM (en-GB CLDR yields "am"/"pm").
    if (getUiLanguage() === 'en') {
      period = String(period).toUpperCase();
    }
    return period;
  } catch {
    return hour >= 12 ? 'PM' : 'AM';
  }
}

/** Format date + time using regional datetime pattern. */
export function formatDateTimeInput(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : toDateSafe(date);
  if (!d) return '';
  const pattern = getDatetimePattern();
  let result = pattern
    .replace(/yyyy/g, String(d.getFullYear()))
    .replace(/MM/g, pad(d.getMonth() + 1))
    .replace(/dd/g, pad(d.getDate()));

  const hour12 = usesHour12() || /hh:mm a/i.test(pattern);
  if (hour12) {
    const h = d.getHours();
    const period = dayPeriodForHour(h);
    const dh = h % 12 || 12;
    result = result
      .replace(/HH/g, pad(h))
      .replace(/hh/g, pad(dh))
      .replace(/mm/g, pad(d.getMinutes()));
    if (/\ba\b/i.test(result)) {
      result = result.replace(/\ba\b/i, period);
    }
  } else {
    result = result
      .replace(/HH/g, pad(d.getHours()))
      .replace(/mm/g, pad(d.getMinutes()));
  }
  return result;
}

/** Parse regional datetime string to Date. */
export function parseDateTimeInput(str) {
  if (!str) return null;
  const trimmed = String(str).trim();
  const spaceIdx = trimmed.search(/\s+\d/);
  if (spaceIdx <= 0) return parseDateInput(trimmed);

  const dateStr = trimmed.slice(0, spaceIdx).trim();
  const timeStr = trimmed.slice(spaceIdx).trim();
  const d = parseDateInput(dateStr);
  if (!d) return null;

  const time = parseTimePart(timeStr);
  if (!time) return d;
  d.setHours(time.h, time.min, 0, 0);
  return d;
}

/** Regional datetime input → ISO datetime-local YYYY-MM-DDTHH:mm */
export function toISODateTimeFromInput(str) {
  const d = parseDateTimeInput(str);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO datetime → regional datetime input string */
export function toRegionalDateTimeFromISO(iso) {
  if (!iso) return '';
  return formatDateTimeInput(toDateSafe(iso));
}

/** Format HH:mm (24h storage) for display per regional timeFormat */
export function formatTimeFromHhMm(hhmm, timeFormatOverride) {
  if (!hhmm) return '';
  const parts = String(hhmm).trim().split(':');
  if (parts.length < 2) return hhmm;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return hhmm;
  const hour12 = timeFormatOverride != null
    ? timeFormatOverride === 'HOUR_12'
    : usesHour12();
  if (hour12) {
    const period = dayPeriodForHour(h);
    const dh = h % 12 || 12;
    return `${pad(dh)}:${pad(m)} ${period}`;
  }
  return `${pad(h)}:${pad(m)}`;
}

/** Format time only — ISO datetime, Date, or HH:mm string (academy timezone for instants). */
export function formatTime(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(value.trim())) {
    return formatTimeFromHhMm(value.slice(0, 5));
  }
  const d = toDateSafe(value);
  if (!d) return String(value);
  if (isWallTimeValue(value)) {
    return formatTimeFromHhMm(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }
  try {
    return new Intl.DateTimeFormat(settings.localeTag || 'en-GB', intlDateTimeOptions({
      hour: '2-digit',
      minute: '2-digit',
    })).format(d);
  } catch {
    return formatTimeFromHhMm(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }
}

/** Format date only (no time) — uses regional date pattern */
export function formatDate(value) {
  if (!value) return '';
  const d = toDateSafe(value);
  if (!d) return '';
  return formatDateInput(d);
}

/** Format date + time per regional settings (academy timezone for zoned instants). */
export function formatDateTime(value, opts = {}) {
  if (!value) return opts.empty ?? '';
  const d = toDateSafe(value);
  if (!d) return String(value);
  if (!isWallTimeValue(value)) {
    try {
      return new Intl.DateTimeFormat(settings.localeTag || 'en-GB', intlDateTimeOptions({
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })).format(d);
    } catch {
      /* fall through to wall-time formatter */
    }
  }
  if (opts.dateSep) {
    const formatted = formatDateTimeInput(d);
    const sep = getDatePattern().match(/[^dMy]+/)?.[0] || '/';
    if (sep !== opts.dateSep) {
      return formatted.replace(new RegExp(`\\${sep}`, 'g'), opts.dateSep);
    }
  }
  return formatDateTimeInput(d);
}

function formatWallCalendarDate(d, intlOptions = {}) {
  if (!d) return '';
  const probe = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0));
  try {
    return new Intl.DateTimeFormat(localeForIntlOptions(intlOptions), {
      ...intlOptions,
      timeZone: getAcademyTimeZone(),
    }).format(probe);
  } catch {
    return formatDateInput(d);
  }
}

/** Intl date/time; named months/weekdays follow UI language, numeric order follows regional locale. */
export function intlFormatDate(value, options = {}) {
  const d = toDateSafe(value);
  if (!d) return '';
  if (value instanceof Date || isWallTimeValue(value)) {
    return formatWallCalendarDate(d, options);
  }
  try {
    return new Intl.DateTimeFormat(localeForIntlOptions(options), {
      ...options,
      timeZone: getAcademyTimeZone(),
    }).format(d);
  } catch {
    return formatDateInput(d);
  }
}

/** @deprecated Prefer formatDate — kept as alias so all UI uses Regional Settings dateFormat. */
export function formatDateLong(value) {
  return formatDate(value);
}

/** @deprecated Prefer formatDate — kept as alias so all UI uses Regional Settings dateFormat. */
export function formatDateMedium(value) {
  return formatDate(value);
}

/** @deprecated Prefer formatDateTime — kept as alias so all UI uses Regional Settings datetimeFormat. */
export function formatDateTimeShort(value) {
  return formatDateTime(value);
}

/** Month name for calendar headers (UI language). */
export function formatMonthYear(value) {
  const d = toDateSafe(value);
  if (!d) return '';
  return formatWallCalendarDate(d, { month: 'long' });
}

/**
 * Month label from API sort key `yyyy-MM` (e.g. academy revenue monthly rows).
 * Prefer this over server-preformatted month strings which use regional locale.
 */
export function formatMonthYearFromSortKey(sortKey) {
  const key = String(sortKey || '').trim();
  if (!/^\d{4}-\d{2}$/.test(key)) return '';
  return formatMonthYear(`${key}-01`);
}

/** Week range label for calendars (UI language for month names). */
export function formatWeekRange(start, end) {
  const a = toDateSafe(start);
  const b = toDateSafe(end);
  if (!a || !b) return '';
  const left = formatWallCalendarDate(a, { day: 'numeric', month: 'short' });
  const right = formatWallCalendarDate(b, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${left} – ${right}`;
}

/** Labels for time picker dropdown options */
export function formatTimeOptionLabel(hhmmValue) {
  return formatTimeFromHhMm(hhmmValue);
}

export function defaultDatetimeFormatForTimeFormat(timeFormat, dateFormat) {
  const datePart = dateFormat || settings.dateFormat || 'dd/MM/yyyy';
  return timeFormat === 'HOUR_12' ? `${datePart} hh:mm a` : `${datePart} HH:mm`;
}

// Preferred public names (aliases for clarity in new code)
export const parseRegionalDate = parseDateInput;
export const formatRegionalDate = formatDateInput;
export const toISODateFromRegional = toISODateFromInput;
export const toRegionalDateFromRegional = toRegionalDateFromISO;
